import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://kakobuyspreadsheet.website";
const FAVICON_URL = "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png";

export default function ShoeFindsPage() {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Best Kakobuy Spreadsheet Shoe Finds: Sneakers & Footwear Guide 2026",
    "description": "Explore the hottest sneaker and footwear finds from our Kakobuy spreadsheet with verified QC photos, sizing advice, and trusted seller recommendations for every style.",
    "author": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet Team"
    },
    "datePublished": "2026-06-25",
    "dateModified": "2026-06-25",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": SITE_URL + "/guide/shoe-finds"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet",
      "logo": {
        "@type": "ImageObject",
        "url": FAVICON_URL
      }
    },
    "image": "https://readdy.ai/api/search-image?query=A%20pair%20of%20stylish%20retro%20sneakers%20in%20cream%20and%20camel%20tones%20displayed%20on%20a%20minimalist%20wooden%20shelf%20with%20a%20small%20potted%20succulent%20beside%20them%20soft%20natural%20sunlight%20from%20a%20large%20window%20casting%20gentle%20shadows%20editorial%20sneaker%20photography%20with%20warm%20earthy%20atmosphere&width=1400&height=600&seq=article-shoes-v2-04&orientation=landscape"
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-foreground-900 transition-colors mb-6 cursor-pointer">
              <i className="ri-arrow-left-line text-sm"></i>Back to Kakobuy Spreadsheet Home
            </Link>
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold mb-4">Shoe Finds</span>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4 text-balance">
              Best <span className="text-primary-500">Kakobuy Spreadsheet</span> Shoe Finds: Sneakers &amp; Footwear Guide
            </h1>
            <div className="flex items-center gap-4 text-xs text-foreground-500 mb-8 pb-6 border-b border-background-200/70">
              <span>Last Updated: June 25, 2026</span><span className="w-1 h-1 rounded-full bg-foreground-400"></span><span>6 min read</span>
            </div>
          </div>

          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <div className="relative rounded-2xl overflow-hidden aspect-[21/9] mb-8">
              <img src="https://readdy.ai/api/search-image?query=A%20pair%20of%20stylish%20retro%20sneakers%20in%20cream%20and%20camel%20tones%20displayed%20on%20a%20minimalist%20wooden%20shelf%20with%20a%20small%20potted%20succulent%20beside%20them%20soft%20natural%20sunlight%20from%20a%20large%20window%20casting%20gentle%20shadows%20editorial%20sneaker%20photography%20with%20warm%20earthy%20atmosphere&width=1400&height=600&seq=article-shoes-v2-04&orientation=landscape"
                alt="Best Kakobuy Spreadsheet Shoe Finds" className="w-full h-full object-cover rounded-2xl" loading="lazy" />
            </div>

            <section className="mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">Why Shoes Are the Most Popular Kakobuy Category</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                The shoe category dominates the <strong>Kakobuy Spreadsheet</strong> with over 2,400 verified listings, making it the most actively curated section of our entire resource. Sneakers, boots, casual shoes, and athletic footwear are consistently the most sought-after items from Kakobuy shoppers worldwide.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Our <strong>Kakobuy Spreadsheet</strong> shoe section includes detailed sizing conversion charts that translate Asian shoe sizes to US, UK, and EU standards. This is critical because shoes are the most size-sensitive category — ordering the wrong size means a completely unusable product. Every shoe listing in the spreadsheet includes verified fit notes from actual buyers.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">Popular Shoe Styles in the Spreadsheet</h2>
              <div className="space-y-3">
                {[
                  { style: "Retro Runners", desc: "Vintage-inspired sneakers in neutral colorways are trending heavily. The Kakobuy Spreadsheet features dozens of highly-rated retro runner listings with excellent QC photos showing detailed sole construction and material quality." },
                  { style: "Minimalist Court Shoes", desc: "Clean, simple court-style sneakers in white, cream, and light gray dominate the casual footwear section. These versatile shoes pair with almost any outfit and consistently receive the highest seller ratings." },
                  { style: "Chunky Dad Shoes", desc: "The bold chunky sneaker trend continues to thrive on Kakobuy. Our spreadsheet highlights the best-reviewed options with detailed weight and comfort assessments from the community." },
                ].map((s, i) => (
                  <div key={i} className="p-4 rounded-xl bg-background-100 border border-background-200/70">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">{s.style}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{s.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer">
                <i className="ri-home-4-line text-sm"></i>Back to Homepage
              </Link>
              <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-foreground-200 text-foreground-700 text-sm font-medium rounded-lg hover:border-foreground-400 transition-all duration-300 whitespace-nowrap cursor-pointer">
                Browse Spreadsheet <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}